import { useEffect, useState } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { ChevronsLeft, ChevronsRight, Globe, Search, Sun, Moon, Bell, ChevronDown, LogOut, Bug } from 'lucide-react'
import { useMsal } from '@azure/msal-react'
import { useUIStore } from '@/stores/uiStore'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import FeedbackDrawer from '@/components/FeedbackDrawer'

const SUB_NAV: Record<string, string[]> = {
  '/monitoring': ['Overview', 'Run history', 'Alert rules', 'Inventory'],
  '/contracts':  ['All contracts', 'Renewals', 'Templates', 'Reports'],
  '/process':    ['Active instances', 'Templates', 'Library'],
  '/planning':   ['All projects', 'My projects', 'Backlog', 'Reports'],
  '/time':       ['My week', 'Approvals', 'Team utilisation', 'Reports'],
}

interface TopbarProps {
  user: { name?: string; username?: string } | undefined
}

export default function Topbar({ user }: TopbarProps) {
  const pathname   = useRouterState({ select: (s) => s.location.pathname })
  const { theme, toggleTheme, sidebarCollapsed, toggleSidebar, toggleMobileNav } = useUIStore()
  const { instance } = useMsal()
  const [activeSub,      setActiveSub]      = useState(0)
  const [bugDrawerOpen,  setBugDrawerOpen]  = useState(false)

  const signOut = () => instance.logoutRedirect({ postLogoutRedirectUri: '/' })

  const subNav    = SUB_NAV[pathname] ?? []
  const initials  = getInitials(user?.name ?? user?.username ?? 'U')
  const firstName = (user?.name ?? user?.username ?? 'User').split(/[\s@]/)[0]

  useEffect(() => { setActiveSub(0) }, [pathname])

  return (
    <div className="topbar">

      {/* Brand — tappable on mobile to open nav sheet */}
      <div
        className={`brand${sidebarCollapsed ? ' collapsed' : ''}`}
        onClick={toggleMobileNav}
        style={{ cursor: 'pointer' }}
      >
        <div className="brand-mark">
          <svg width="20" height="20" viewBox="0 0 64 64" style={{ display: 'block' }}>
            <rect width="64" height="64" rx="14" fill="var(--ns-text)" />
            <path d="M20 40 L44 40 L40 48 L24 48 Z" fill="var(--ns-brand)" fillOpacity="0.55" />
            <path d="M19 28 L45 28 L41 36 L23 36 Z" fill="var(--ns-brand)" fillOpacity="0.8" />
            <path d="M18 16 L46 16 L42 24 L22 24 Z" fill="var(--ns-brand)" />
          </svg>
        </div>
        {!sidebarCollapsed && (
          <div className="brand-text">number<span className="ns-suffix">skills</span></div>
        )}
      </div>

      {/* Collapse toggle — hidden on mobile */}
      <button
        className="btn btn-ghost btn-icon btn-sm topbar-toggle"
        onClick={toggleSidebar}
        title="Toggle sidebar"
      >
        {sidebarCollapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
      </button>

      {/* Scope picker */}
      <button className="scope-pill">
        <Globe size={14} style={{ color: 'var(--ns-text-muted)' }} />
        <span className="scope-label">Scope:</span>
        <span style={{ fontWeight: 500 }}>All customers</span>
        <ChevronDown size={12} style={{ color: 'var(--ns-text-muted)' }} />
      </button>

      {/* Sub-nav — hidden on mobile */}
      {subNav.length > 0 && (
        <div className="topbar-subnav row" style={{ gap: 2, flexShrink: 0 }}>
          <span className="muted" style={{ fontSize: 12, flexShrink: 0 }}>·</span>
          <div className="row" style={{ gap: 2, fontSize: 13 }}>
            {subNav.map((s, i) => (
              <button
                key={s}
                className="btn btn-ghost btn-sm"
                onClick={() => setActiveSub(i)}
                style={{
                  fontWeight: i === activeSub ? 600 : 500,
                  color: i === activeSub ? 'var(--ns-text)' : 'var(--ns-text-muted)',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search box — hidden on mobile */}
      <div className="search-box topbar-search">
        <Search size={14} />
        <input className="input" placeholder="Search anything…" readOnly />
        <span className="kbd">⌘K</span>
      </div>

      {/* Theme toggle — hidden on mobile */}
      <button className="btn btn-ghost btn-icon topbar-theme" onClick={toggleTheme} title="Toggle theme">
        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
      </button>

      {/* Notifications — hidden on mobile */}
      <button className="btn btn-ghost btn-icon topbar-notif" title="Notifications">
        <Bell size={15} />
      </button>

      {/* User chip */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="user-chip" style={{ cursor: 'pointer' }}>
            <div className="avatar">{initials}</div>
            <div className="col" style={{ gap: 0 }}>
              <span style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.2 }}>{firstName}</span>
              <span className="muted" style={{ fontSize: 10.5, lineHeight: 1.2 }}>Admin</span>
            </div>
            <ChevronDown size={12} style={{ color: 'var(--ns-text-muted)' }} />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" style={{ minWidth: 180 }}>
          <div style={{ padding: '6px 8px 4px', fontSize: 12, color: 'var(--ns-text-muted)' }}>
            {user?.name ?? user?.username}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setBugDrawerOpen(true)}>
            <Bug size={14} style={{ marginRight: 8 }} />
            Report a bug
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={signOut}>
            <LogOut size={14} style={{ marginRight: 8 }} />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <FeedbackDrawer open={bugDrawerOpen} onClose={() => setBugDrawerOpen(false)} />
    </div>
  )
}

function getInitials(name: string) {
  return name.split(/[\s@]/).filter(Boolean).map(p => p[0]).join('').toUpperCase().slice(0, 2)
}
